import {
  onMounted,
  onBeforeUnmount,
  ref,
  shallowRef,
  getCurrentInstance,
} from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import getUuid from 'widget/helpers/uuid';
import { convertAudio } from './utils/audioConversionUtils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

export const useAudioRecorder = (props, emit) => {
  const { uid } = getCurrentInstance();
  const waveformContainer = ref(null);
  const wavesurfer = shallowRef(null);
  const record = shallowRef(null);
  let disposed = false;
  let isRecording = false;
  let hasRecording = false;
  let recordedAudioUrl = null;

  const reportError = (error, phase) => {
    if (disposed) return;
    if (phase === 'playback') {
      wavesurfer.value.pause();
      emit('pause');
    }
    emit('recordError', { error, phase });
  };

  const prepareRecording = async blob => {
    isRecording = false;
    record.value.stopMic();
    if (disposed) return;
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      if (disposed) return;
      const audioType = audioBlob.type.split(';')[0].trim();
      const extension = AUDIO_EXTENSION_MAP[audioType];
      if (!extension) throw new Error('Unsupported recorded audio format');
      const file = new File([audioBlob], `${getUuid()}.${extension}`, {
        type: audioType,
      });
      if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
      recordedAudioUrl = URL.createObjectURL(audioBlob);
      await wavesurfer.value.load(recordedAudioUrl);
      if (disposed) return;
      hasRecording = true;
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
    } catch (error) {
      hasRecording = false;
      reportError(error, 'conversion');
    }
  };

  const startRecording = async () => {
    try {
      await record.value.startRecording();
      if (disposed) {
        record.value.stopRecording();
        record.value.stopMic();
        // startMic may have installed context/timer cleanup after destruction.
        record.value.destroy();
        return;
      }
      isRecording = true;
      emit('recordingStarted');
    } catch (error) {
      record.value.stopMic();
      reportError(error, 'microphone');
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    isRecording = false;
    record.value.stopRecording();
    record.value.stopMic();
  };

  const playPause = async () => {
    if (!hasRecording || disposed) return;
    try {
      await wavesurfer.value.playPause();
    } catch (error) {
      reportError(error, 'playback');
    }
  };

  useEmitter('pause_playing_audio', currentPlayingId => {
    if (currentPlayingId !== uid && hasRecording) wavesurfer.value.pause();
  });

  onMounted(() => {
    const options = { scrollingWaveform: true, renderRecordedAudio: false };
    if (
      props.audioRecordFormat === 'audio/ogg' &&
      MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
    ) {
      options.mimeType = 'audio/ogg;codecs=opus';
    }
    wavesurfer.value = WaveSurfer.create({
      container: waveformContainer.value,
      waveColor: '#1F93FF',
      progressColor: '#6E6F73',
      height: props.height,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      plugins: [RecordPlugin.create(options)],
    });
    record.value = wavesurfer.value.plugins[0];
    wavesurfer.value.on('pause', () => emit('pause'));
    wavesurfer.value.on('play', () => {
      emitter.emit('pause_playing_audio', uid);
      emit('play');
    });
    wavesurfer.value.on('finish', () => emit('pause'));
    record.value.on('record-end', prepareRecording);
    record.value.on('record-progress', time => {
      const seconds = Math.floor(time / 1000);
      const minutesLabel = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
      const secondsLabel = (seconds % 60).toString().padStart(2, '0');
      emit('recorderProgressChanged', `${minutesLabel}:${secondsLabel}`);
    });
    startRecording();
  });

  onBeforeUnmount(() => {
    disposed = true;
    record.value?.stopMic();
    wavesurfer.value?.destroy();
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
  });

  return { waveformContainer, playPause, stopRecording, record };
};
